import { z } from 'zod';
import { zUuid } from '@saas/shared';

/**
 * conversation（全モジュール共通の会話基盤・03 §12）の DTO。
 * サマリ型と列挙はリード（契約）が固定。入力の zod スキーマは実装側が追加する（追加は可・改名は不可）。
 */

export const CONVERSATION_KINDS = ['record', 'channel', 'dm'] as const;
export type ConversationKind = (typeof CONVERSATION_KINDS)[number];

/** 既定は「subject があれば inherit、無ければ open」。subject 無しに inherit は付けない（§12.5-4 の CHECK） */
export const VISIBILITIES = ['inherit', 'open', 'private'] as const;
export type Visibility = (typeof VISIBILITIES)[number];

export const MEMBER_ROLES = ['owner', 'member', 'observer'] as const;
export type MemberRole = (typeof MEMBER_ROLES)[number];

export const NOTIFY_LEVELS = ['all', 'mentions', 'none'] as const;
export type NotifyLevel = (typeof NOTIFY_LEVELS)[number];

/**
 * subject_type は各モジュールが SubjectPermissionResolver を登録している種類だけ（§12.5-4）。
 * 段 3 時点: customer / contact（crm）、deal（sales）、project（project）、contract（contract）、
 * workflow_request（workflow）、expense_request（expense）、report（report）。meeting は段 4
 */
export const zSubjectType = z.string().regex(/^[a-z][a-z0-9_]{1,40}$/);

export interface ConversationSummary {
  id: string;
  kind: ConversationKind;
  subjectType: string | null;
  subjectId: string | null;
  title: string;
  visibility: Visibility;
  displayAsChannel: boolean;
  subjectClosedAt: Date | null;
  archivedAt: Date | null;
  lastMessageAt: Date | null;
  messageCount: number;
  /** 人間が始めた会話の作成者（システムが作ったなら null）。段 4 後半に追加（既存の呼び出し側を壊さないよう optional） */
  createdBy?: string | null;
  createdAt: Date;
}

export interface MessageSummary {
  id: string;
  conversationId: string;
  parentId: string | null;
  /** null = システム投稿 */
  authorId: string | null;
  authorName: string | null;
  authorModule: string | null;
  body: string | null;
  bodyFormat: 'markdown' | 'plain';
  eventType: string | null;
  eventPayload: unknown;
  mentions: string[];
  replyCount: number;
  editedAt: Date | null;
  deletedAt: Date | null;
  createdAt: Date;
}

/**
 * 発言者アイコンから表示する最小プロフィール。
 * 会話を読める利用者にだけ MessageDetail の一部として返し、ユーザー一覧を別途公開しない。
 */
export interface MessageAuthorProfile {
  email: string;
  departmentId: string | null;
  departmentName: string | null;
  status: 'invited' | 'active' | 'suspended';
}

/** §12.5-5 移動の結果 */
export type MoveMode = 'split' | 'move_all';
export type MoveResult =
  | { status: 'moved'; conversationId: string }
  | { status: 'split'; archivedId: string; newConversationId: string }
  | { status: 'needs_confirmation'; addedUsers: string[]; defaultMode: MoveMode };

export const zMoveMode = z.enum(['split', 'move_all']);
export const zConversationId = zUuid;

// ============================================================================
// ここから実装側の追加（段 3・conversation 担当）。契約の型・列挙は上のまま
// ============================================================================

/**
 * 例外イベント（§12.10.6a・§12.5-1・§12.6 と 1 対 1 対応）。この 5 つだけが
 * 会話を新規生成してよく（postExceptional）、展開表示され、プッシュが飛び、「自分宛て」に入る（バッジには足さない）。
 * project.cost.added は「粗利が予算を下回った」ときだけ呼び出し元が postExceptional する
 */
export const EXCEPTIONAL_EVENT_TYPES = ['deal.won', 'deal.lost', 'contract.alert.fired', 'meeting.summarized', 'project.cost.added'] as const;
export type ExceptionalEventType = (typeof EXCEPTIONAL_EVENT_TYPES)[number];
export function isExceptionalEventType(eventType: string | null | undefined): eventType is ExceptionalEventType {
  return eventType != null && (EXCEPTIONAL_EVENT_TYPES as readonly string[]).includes(eventType);
}

/** light プランで会話を付けられるレコード（01 §4.1b「商談・顧客のみ」）。それ以外は 403 */
export const LIGHT_PLAN_SUBJECT_TYPES = ['deal', 'customer', 'contact', 'lead'] as const;

/** アクティブなレコード会話がこの件数を超えた人に警告を出す（§12.10.7「上限の可視化」） */
export const ACTIVE_RECORD_CONVERSATION_LIMIT = 20;

/** subject が終わってから自動アーカイブまでの日数、発言が無い非担当者が自動退出するまでの日数（§12.10.7） */
export const AUTO_ARCHIVE_DAYS = 30;
export const AUTO_LEAVE_DAYS = 30;

/** 通知の kind（core.notifications.notify）。web の「自分宛て」はこの kind で区別する（例外イベントはバッジに足さない・§12.10.6） */
export const NOTIFICATION_KIND_MENTION = 'conversation.mention';
export const NOTIFICATION_KIND_EXCEPTIONAL = 'conversation.exceptional';

/** conversation 自身が出すシステム投稿の event_type（split の「以前の経緯」リンク・移動の記録） */
export const SPLIT_EVENT_TYPE = 'conversation.split';
export const SUBJECT_MOVED_EVENT_TYPE = 'conversation.subject_moved';

/** 添付の FileService スコープ（type = 'conversation'、id = 会話 ID）。UI は core の /files/uploads にこれで上げてから投稿に付ける */
export const ATTACHMENT_FILE_SCOPE = 'conversation';

// ---- 出力 DTO ----

export interface SubjectHistoryEntry {
  id: string;
  fromType: string | null;
  fromId: string | null;
  toType: string;
  toId: string;
  mode: MoveMode;
  splitFromConversationId: string | null;
  movedBy: string | null;
  movedByName: string | null;
  movedAt: Date;
}

export interface MembershipSummary {
  role: MemberRole;
  notifyLevel: NotifyLevel;
  autoJoined: boolean;
  joinedAt: Date;
}

export interface ConversationDetail extends ConversationSummary {
  /** 自分が書けるか（archived → false、observer → false、subject の canWrite・§12.5-4） */
  canWrite: boolean;
  /** 自分の未読（ルート投稿。システム投稿は例外イベントだけ数える） */
  unread: number;
  memberCount: number;
  /** 自分の参加状態。参加していなければ null（subject 権限だけで読んでいる） */
  membership: MembershipSummary | null;
  /** subject の移動履歴（「この会話は 3/12 にリードから商談になりました」の材料） */
  history: SubjectHistoryEntry[];
  /** split でこの会話がアーカイブされたとき、続きの会話 */
  continuedInConversationId: string | null;
}

/** GET /conversation/subject/:type/:id の応答。会話が無くても canStart で「会話を始める」を出すか決める */
export interface SubjectConversationView {
  conversation: ConversationDetail | null;
  /** 会話を始められるか（subject の canWrite ∧ プラン ∧ conversation.message.write） */
  canStart: boolean;
}

export interface ReactionSummary {
  emoji: string;
  count: number;
  /** 自分が付けたか */
  reacted: boolean;
  userIds: string[];
}

export interface AttachmentSummary {
  id: string;
  fileId: string;
  filename: string;
  mime: string;
  bytes: number | null;
}

export type SharedItemType = 'file' | 'image' | 'video' | 'audio' | 'link';

interface SharedItemBase {
  id: string;
  type: SharedItemType;
  messageId: string;
  parentId: string | null;
  authorName: string | null;
  createdAt: Date;
}

export interface SharedAttachmentItem extends SharedItemBase {
  type: 'file' | 'image' | 'video' | 'audio';
  attachment: AttachmentSummary;
}

export interface SharedLinkItem extends SharedItemBase {
  type: 'link';
  url: string;
  label: string;
}

export type SharedItem = SharedAttachmentItem | SharedLinkItem;

export interface SharedItemsResult {
  items: SharedItem[];
  /** 安全な上限（共有物を含む投稿 1,000 件）に達した場合。UI は利用者へ知らせる */
  truncated: boolean;
}

export interface MessageDetail extends MessageSummary {
  /** システム投稿・削除済みユーザーは null */
  authorProfile: MessageAuthorProfile | null;
  /** 例外イベント（§12.10.6a）のシステム投稿。UI は畳まずに展開する */
  exceptional: boolean;
  reactions: ReactionSummary[];
  attachments: AttachmentSummary[];
  /** スレッドの最終返信 */
  lastReplyAt: Date | null;
  /** 自分がこのスレッドで未読の返信があるか（ルート投稿のみ） */
  threadUnread: boolean;
  /** 「チャンネルにも投稿」した返信（§12.7）。ルートのタイムラインにも出る */
  alsoToChannel: boolean;
}

export interface ConversationMemberSummary {
  userId: string;
  displayName: string;
  role: MemberRole;
  notifyLevel: NotifyLevel;
  autoJoined: boolean;
  lastPostedAt: Date | null;
  joinedAt: Date;
  /**
   * この人の既読位置（`conversation_reads`・1 会話 1 行）。一度も読んでいなければ両方 null。
   * 05 §5.4a「既読」（自分の発言の下に「既読」）の材料。**会話を読める人には出してよい**
   * （この一覧そのものが既存の権限判定を通った後の情報なので、DM・非公開に限らない）。段 4 後半に追加
   */
  lastReadMessageId: string | null;
  lastReadAt: Date | null;
}

export interface RecordConversationItem {
  conversationId: string;
  title: string;
  subjectType: string;
  subjectId: string;
  unread: number;
  /** 自分宛てのメンションのうち未読の数 */
  mentions: number;
  lastMessageAt: Date | null;
  displayAsChannel: boolean;
}

/**
 * GET /conversation/me = 「自分宛て」の材料（§12.10.8 の ①〜④ を 1 本で返す）。
 * バッジの式（§12.10.6）は conversationBadgeCount() を使う。unreadRecordConversations は未読があるものだけ（0 件なら空）
 */
export interface MyConversationsSummary {
  /** 未読の DM メッセージ数 */
  unreadDm: number;
  /** 自分宛ての未読メンション数（チャンネル・レコード会話を問わず） */
  mentions: number;
  unreadRecordConversations: RecordConversationItem[];
  /** アクティブ（未アーカイブ・参加中）なレコード会話の数と上限。超えていれば UI が警告する（§12.10.7） */
  activeRecordConversations: number;
  activeRecordConversationLimit: number;
}

// ---- 入力 DTO（Zod）----

export const zSubjectParams = z.object({ subjectType: zSubjectType, subjectId: zUuid });

export const zStartConversation = z.object({
  /** 「〇〇商事 / 新社屋工事」。レコード画面が渡す */
  title: z.string().trim().min(1, '必須項目です').max(200),
});
export type StartConversationInput = z.infer<typeof zStartConversation>;

export const zAttachmentRef = z.object({ fileId: zUuid });

/** 1 つの発言でメンションできる人数の上限（`@here` で参加者全員に展開するときもこの数まで） */
export const MENTION_MAX = 50;

export const zPostMessage = z.object({
  body: z.string().max(20_000).default(''),
  bodyFormat: z.enum(['plain', 'markdown']).optional(),
  /** 返信ならルート投稿の ID */
  parentId: zUuid.nullable().optional(),
  mentions: z.array(zUuid).max(MENTION_MAX).default([]),
  /** core の /files/uploads（scopeType = 'conversation'）で上げた fileId */
  attachments: z.array(zAttachmentRef).max(20).default([]),
  /**
   * 「チャンネルにも投稿」（§12.7・段 4 後半の追加）。返信のときだけ有効で、
   * その返信をスレッドと会話のタイムラインの両方に出す（本文は 1 行のまま）。
   * 既存の呼び出しを壊さないよう optional（未指定 = false）
   */
  alsoToChannel: z.boolean().optional(),
});
export type PostMessageInput = z.infer<typeof zPostMessage>;

export const zEditMessage = z.object({ body: z.string().trim().min(1, '必須項目です').max(20_000) });
export type EditMessageInput = z.infer<typeof zEditMessage>;

export const zReaction = z.object({ emoji: z.string().trim().min(1).max(32) });
export type ReactionInput = z.infer<typeof zReaction>;

export const zMarkRead = z.object({ lastReadMessageId: zUuid.nullable().optional() });
export type MarkReadInput = z.infer<typeof zMarkRead>;

const UUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

/**
 * base64url → 文字列（中身は UUID = ASCII のみ）。`@saas/db` の `encodeCursor` の逆。
 * この DTO はブラウザにも入るので Node の Buffer に依存しない（atob → Buffer の順に使う）
 */
function decodeBase64UrlAscii(value: string): string | null {
  try {
    const b64 = value.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64.length % 4 === 0 ? b64 : b64 + '='.repeat(4 - (b64.length % 4));
    const g = globalThis as { atob?: (s: string) => string; Buffer?: { from(s: string, enc: string): { toString(enc: string): string } } };
    if (typeof g.atob === 'function') return g.atob(padded);
    return g.Buffer ? g.Buffer.from(padded, 'base64').toString('utf8') : null;
  } catch {
    return null;
  }
}

/**
 * 発言の一覧のカーソル（段 4 後半に追加）。**`MessagePage.nextCursor`（base64url）でも、発言の ID（生の UUID）でも受ける**。
 * 以前は `before` が生の UUID しか受けず、`nextCursor` をそのまま渡すと 400 になっていた（アプリの「さらに読み込む」が動かない）。
 * どちらの形でも最後は UUID に正規化してサービス層へ渡す
 */
export const zMessageCursor = z
  .string()
  .trim()
  .min(1)
  .max(200)
  .transform((v) => (UUID_RE.test(v) ? v : (decodeBase64UrlAscii(v) ?? v)))
  .pipe(zUuid);

export const zMessageListQuery = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(50),
  /** この ID より古いルート投稿（さらに読み込む）。生の UUID でも `nextCursor` でもよい */
  before: zMessageCursor.optional(),
});
export type MessageListQuery = z.infer<typeof zMessageListQuery>;

export const zRecordListQuery = z.object({
  filter: z.enum(['unread', 'all']).default('unread'),
  subjectType: zSubjectType.optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  cursor: z.string().optional(),
});
export type RecordListQuery = z.infer<typeof zRecordListQuery>;

export const zAddMembers = z.object({
  userIds: z.array(zUuid).min(1).max(100),
  role: z.enum(['member', 'observer']).default('member'),
});
export type AddMembersInput = z.infer<typeof zAddMembers>;

export const zUpdateMembership = z.object({ notifyLevel: z.enum(NOTIFY_LEVELS) });
export type UpdateMembershipInput = z.infer<typeof zUpdateMembership>;

export const zUpdateConversation = z.object({
  title: z.string().trim().min(1).max(200).optional(),
  /** private は「絞る」ためだけ。open は subject があれば inherit と同じ扱い（§12.5-4） */
  visibility: z.enum(VISIBILITIES).optional(),
  /** 昇格（表示だけ。kind は変えない・§12.5-2） */
  displayAsChannel: z.boolean().optional(),
});
export type UpdateConversationInput = z.infer<typeof zUpdateConversation>;

/** subject を持たないチャンネル / DM の作成（画面は段 4 の chat。段 3 は API だけ） */
export const zCreateChannel = z.object({
  kind: z.enum(['channel', 'dm']),
  title: z.string().trim().min(1).max(200),
  visibility: z.enum(['open', 'private']).optional(),
  memberIds: z.array(zUuid).max(500).default([]),
});
export type CreateChannelInput = z.infer<typeof zCreateChannel>;

// ============================================================================
// 段 4 後半の追加（decisions.md §J-3）。chat（UI 層）が conversation の表を直接 SELECT しないための
// 読み取り DTO。既存の型・列挙・Zod は変えない（追加のみ）
// ============================================================================

/** 参加者 1 行（公開 I/F 用の最小形。表示名・在席は呼び出し側が core から引く） */
export interface ConversationMemberRef {
  conversationId: string;
  userId: string;
  role: MemberRole;
  notifyLevel: NotifyLevel;
  autoJoined: boolean;
  joinedAt: Date;
}

/** メッセージ 1 行（公開 I/F 用の最小形。削除済みでも行は返す（本文は残る）ので呼び出し側が deletedAt を見る） */
export interface MessageRef {
  id: string;
  conversationId: string;
  parentId: string | null;
  authorId: string | null;
  authorModule: string | null;
  body: string | null;
  bodyFormat: 'markdown' | 'plain';
  eventType: string | null;
  mentions: string[];
  alsoToChannel: boolean;
  deletedAt: Date | null;
  createdAt: Date;
}

/** 参加中の DM / チャンネル 1 行（chat のサイドバー・§12.10.8 ①②） */
export interface MyConversationItem {
  conversationId: string;
  kind: ConversationKind;
  title: string;
  visibility: Visibility;
  /** レコード会話のとき（昇格して一覧に混ざったもの）だけ値が入る */
  subjectType: string | null;
  subjectId: string | null;
  displayAsChannel: boolean;
  archivedAt: Date | null;
  lastMessageAt: Date | null;
  /** 最後の人間の発言の先頭 140 文字（システム投稿は出さない） */
  lastExcerpt: string | null;
  memberCount: number;
  /** DM の相手（2 人以上なら最初の 1 人）。チャンネル・レコード会話は null */
  partnerId: string | null;
  /** 参加していなければ null（filter = 'all' で見えている公開チャンネル） */
  role: MemberRole | null;
  notifyLevel: NotifyLevel | null;
  /** 未読のルート投稿（+「チャンネルにも投稿」した返信）。参加していなければ 0 */
  unread: number;
  /** 自分宛ての未読メンション */
  mentions: number;
}

export interface MyConversationQuery {
  /** 既定は ['dm', 'channel'] */
  kinds?: readonly ConversationKind[];
  /**
   * さらに読む（段 5 の追加・§J-3）。`listMyConversationsPage` が返す `nextCursor` をそのまま渡す。
   * `listMyConversations`（配列を返す旧い口）では無視される
   */
  cursor?: string | null;
  /** joined = 参加中だけ（既定）/ all = 参加できる公開チャンネルも（レコード会話は常に参加中のみ） */
  filter?: 'joined' | 'all';
  includeArchived?: boolean;
  /** チャンネル表示に昇格したレコード会話（display_as_channel・§12.5-2）も混ぜる。参加中のものだけ */
  includePromotedRecords?: boolean;
  limit?: number;
}

/** 参加中のスレッド 1 件（Threads 相当・§12.7） */
export interface MyThreadItem {
  rootMessageId: string;
  conversationId: string;
  conversationKind: ConversationKind;
  conversationTitle: string;
  subjectType: string | null;
  subjectId: string | null;
  body: string | null;
  authorId: string | null;
  createdAt: Date;
  replyCount: number;
  lastReplyAt: Date | null;
  participantIds: string[];
  /** thread_reads から見た未読（会話の未読とは別・§12.7） */
  unread: boolean;
}

export interface MyThreadQuery {
  limit?: number;
  unreadOnly?: boolean;
}

/** 本文検索の 1 件。重み付け・並び（§12.10.9）は呼び出し側（chat）が決める */
export interface MessageSearchHit {
  messageId: string;
  conversationId: string;
  conversationKind: ConversationKind;
  conversationTitle: string;
  subjectType: string | null;
  subjectId: string | null;
  parentId: string | null;
  body: string | null;
  authorId: string | null;
  createdAt: Date;
}

export interface MessageSearchResult {
  items: MessageSearchHit[];
  /** 権限で絞った後の kind ごとの件数（limit で切る前） */
  counts: Record<ConversationKind, number>;
}

export interface MessageSearchQuery {
  /** 部分一致の検索語（LIKE のメタ文字はサービス側でエスケープする） */
  q: string;
  /** 既定は 3 系統すべて */
  kinds?: readonly ConversationKind[];
  limit?: number;
}

/** バッジ（§12.10.6）の材料。conversationBadgeCount() にそのまま渡せる */
export interface UnreadCounts {
  unreadDm: number;
  mentions: number;
}

/** 参加者を外す理由（監査ログの after に残す。異動・退職・招待の取り消し） */
export const MEMBER_REMOVE_REASONS = ['manual', 'department_changed', 'deactivated', 'sync'] as const;
export type MemberRemoveReason = (typeof MEMBER_REMOVE_REASONS)[number];

// ============================================================================
// 段 5 の追加: システム投稿の種類ごとの ON / OFF（§12.5-3・decisions.md §H-3）と「自分宛て」の一覧（§12.10.3）
// ============================================================================

/**
 * 管理画面に並べるシステム投稿の種類（§12.6 で各モジュールが投稿するもの）。
 * ここに無い種類も `postSystem` が使われれば管理画面に出る（実際に投稿された eventType を DB から拾って足す）。
 * label はテナント管理者に見せる日本語
 */
export const SYSTEM_POST_EVENT_CATALOG: ReadonlyArray<{ eventType: string; label: string; module: string }> = [
  { eventType: 'deal.stage_changed', label: '商談のステージが変わった', module: 'sales' },
  { eventType: 'quote.sent', label: '見積を送った', module: 'sales' },
  { eventType: 'sales.deal.stale', label: '停滞している商談', module: 'sales' },
  { eventType: 'sales.deal.won', label: '受注', module: 'sales' },
  { eventType: 'sales.deal.lost', label: '失注', module: 'sales' },
  { eventType: 'crm.lead.inquiry_received', label: '新しい問い合わせ', module: 'crm' },
  { eventType: 'crm.lead.unassigned', label: '未割当の問い合わせ', module: 'crm' },
  { eventType: 'crm.lead.overdue', label: '初回対応の期限超過', module: 'crm' },
  { eventType: 'report.submitted', label: '日報が提出された', module: 'report' },
  { eventType: 'chat.workflow.submitted', label: '発言から稟議を起票した', module: 'chat' },
  { eventType: 'chat.task.linked', label: '発言からタスクを作った', module: 'chat' },
  // gap-comms（03 §11.4 #3・§12.6）: 電話連携の通話（着信・発信・不在）。call.completed / call.linked を conversation が購読して流す
  { eventType: 'call.completed', label: '電話の通話（着信・発信・不在着信）', module: 'telephony' },
];

/**
 * 切れないシステム投稿: 例外イベント（§12.10.6a の 5 つ）と、conversation 自身が残す記録（split / 移動）。
 * 前者は「通知して当然のこと」、後者は会話のつながりが読めなくなるため
 */
export const ALWAYS_ON_SYSTEM_EVENT_TYPES: readonly string[] = [...EXCEPTIONAL_EVENT_TYPES, SPLIT_EVENT_TYPE, SUBJECT_MOVED_EVENT_TYPE, 'chat.webhook'];

export function isAlwaysOnSystemEventType(eventType: string | null | undefined): boolean {
  return eventType != null && ALWAYS_ON_SYSTEM_EVENT_TYPES.includes(eventType);
}

/** 管理画面の 1 行（行が無い種類は enabled = true = 既定） */
export interface SystemPostSetting {
  eventType: string;
  label: string;
  module: string | null;
  enabled: boolean;
  /** 例外イベントなど、OFF にできない種類 */
  locked: boolean;
  updatedAt: Date | null;
}

export const zSystemPostSetting = z.object({
  eventType: z.string().trim().min(1).max(100),
  enabled: z.boolean(),
});
export type SystemPostSettingInput = z.infer<typeof zSystemPostSetting>;

/** 「自分宛て」（§12.10.3）の 1 行。未読の DM と、自分宛ての未読メンション */
export interface MyInboxItem {
  kind: 'dm' | 'mention';
  conversationId: string;
  conversationKind: ConversationKind;
  /** DM は相手の表示名（複数人なら会話の題名）、それ以外は会話の題名 */
  title: string;
  subjectType: string | null;
  subjectId: string | null;
  /** メンションのときは該当の発言（DM は最後の未読の発言） */
  messageId: string | null;
  parentId: string | null;
  authorId: string | null;
  authorName: string | null;
  excerpt: string | null;
  at: Date | null;
  /** DM の未読件数（メンションは 1） */
  unread: number;
}

export const zMyInboxQuery = z.object({ limit: z.coerce.number().int().min(1).max(50).default(5) });
export type MyInboxQuery = z.infer<typeof zMyInboxQuery>;
